<script setup lang="ts">
  import ProgressBarQC from '@qicgroupdoha/qic-connect-ds/components/ProgressBarQC'
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { useGoogleAnalytics } from '@/shared/composables'
  import type { Locale } from '@/shared/types/common.types'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'

  interface ProgressMapping {
    progressStep: number
    substep?: number
  }

  interface Props {
    withProgressBar?: boolean
  }

  withDefaults(defineProps<Props>(), {
    withProgressBar: true,
  })

  const currentFlowStep = defineModel<number>('currentStep', { default: 0 })
  const { t, locale } = useI18n()
  const route = useRoute()
  const { getHomeUrl } = useHomeUrl()
  const homeUrl = getHomeUrl()
  const gaService = useGoogleAnalytics()

  const FLOW_TO_PROGRESS: ProgressMapping[] = [
    { progressStep: 1, substep: 1 },
    { progressStep: 1, substep: 2 },
    { progressStep: 1, substep: 3 },
    { progressStep: 2 },
    { progressStep: 3 },
    { progressStep: 4, substep: 1 },
    { progressStep: 4, substep: 2 },
    { progressStep: 5 },
  ]

  const mapping = computed(() => FLOW_TO_PROGRESS[currentFlowStep.value - 1] ?? FLOW_TO_PROGRESS[0])

  const progressBarStep = computed(() => mapping.value.progressStep)

  const steps = computed(() => [
    {
      title: t('travel.common.progress_bar.step_1'),
      substeps: { current: mapping.value.substep ?? 1, total: 3 },
    },
    { title: t('travel.common.progress_bar.step_2') },
    { title: t('travel.common.progress_bar.step_3') },
    {
      title: t('travel.common.progress_bar.step_4'),
      substeps: { current: mapping.value.substep ?? 1, total: 2 },
    },
    { title: t('travel.common.progress_bar.step_5') },
  ])

  const onLogoClick = () => {
    gaService.sendToMainPage(route.name as AvailableFlowRoutes)
  }
</script>

<template>
  <div class="flow-header-qc" :class="{ 'flow-header-qc-only-logo': !withProgressBar }">
    <a :href="homeUrl" data-test="logo-link" class="flow-header-qc__logo" @click="onLogoClick">
      <svg
        width="76"
        height="24"
        viewBox="0 0 76 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.4132 1.54686C23.3726 1.54686 25.8493 4.07836 25.8493 7.6807C25.8493 11.4388 23.3726 14.0286 17.8776 14.0092C13.2146 14.0092 10.8346 11.1272 10.2542 8.07015H5.62988C5.82331 11.9062 9.98334 15.2749 18.1291 15.2749C26.333 15.2749 30.4543 23.0833 40.5543 23.0833C46.3203 23.0833 49.8029 21.2723 52.1634 16.93H51.2928C49.2032 19.7924 47.2682 20.9997 42.7794 20.9997C34.4596 20.9997 30.1833 14.6906 20.4122 14.6906V14.8854C28.5001 13.8145 31.2088 11.1662 31.2088 7.6807C31.2088 3.78626 27.4165 0.28125 19.5029 0.28125C15.1494 0.28125 11.7248 1.2743 9.77045 2.57893V3.57198C12.2278 2.36469 14.6657 1.54686 17.4132 1.54686ZM41.3088 14.1065V1.44953L45.5655 1.25486V0.475916H31.7313V1.25486L35.9879 1.44953V14.1065L31.7313 14.3013V15.0801H45.5655V14.3013L41.3088 14.1065ZM70.3706 10.8158C67.7391 12.393 64.3145 13.6197 60.5415 13.6197C54.234 13.6197 51.4476 11.3609 51.4476 7.6807C51.4476 4.07836 54.2144 1.54686 59.8837 1.54686C62.9794 1.54686 65.8043 2.24792 68.4939 3.57198V2.57893C66.4043 1.19641 62.573 0.28125 57.7939 0.28125C49.8805 0.28125 46.088 3.78626 46.088 7.6807C46.088 11.9646 49.8029 15.2749 58.3359 15.2749C63.173 15.2749 67.7391 13.8339 70.3706 11.8283V10.8158Z"
          fill="#231844"
        />
      </svg>
    </a>
    <ProgressBarQC
      v-if="withProgressBar && currentFlowStep > 0"
      v-model="progressBarStep"
      :locale="locale as Locale"
      :steps="steps"
      variant="named"
      class="flow-header-qc__progress-bar"
      data-test="progress-bar"
    />
  </div>
</template>

<style lang="scss" scoped>
  @use '@/shared/assets/styles/mixins';

  .flow-header-qc {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-5);
    position: relative;
    width: 100%;
    max-width: 1440px;
    padding-block: 0 var(--spacing-7);
    padding-inline: var(--spacing-11);

    @media (width <= 1088px) {
      padding-inline: var(--spacing-7);
    }

    @include mixins.mobile-flow {
      flex-direction: column;
      padding: 0;
    }
  }

  .flow-header-qc__progress-bar {
    max-width: 782px;
    margin: 0 auto;
    padding: var(--spacing-3) var(--spacing-5);

    @include mixins.mobile-flow {
      padding: var(--spacing-5);
    }
  }

  .flow-header-qc__logo {
    @include mixins.mobile-flow {
      margin-block: var(--spacing-5);
    }
  }
</style>

<style lang="scss">
  .flow-header-qc {
    .ds-progress-bar-named__wrapper {
      align-items: flex-start;
      gap: var(--spacing-3);

      .ds-progress-bar-named__title {
        text-align: initial;
        padding-inline-start: var(--spacing-2);
      }
    }
  }
</style>
